import React, { ReactNode, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { CalendarList } from '../../../../components/society/CalendarList'
import { ViewBase } from '../../../../components/common/ViewBase'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import { ButtonVariant } from '../../../../components/common/Button'
import { CreateUpdateEventModal } from '../../../../components/society/Event/CreateUpdateEventModal'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { boardRoomEventQueries } from '../../../../api/boardroom-events'
import { IBoardRoomEventModel } from '../../../../interfaces/models/boardroom-events.interfaces'
import GetToKnow from '../../../../components/society/GetToKnow/GetToKnow'
import { i18nSocietyType } from '../../../../i18n'

export enum ActiveTab {
  UPCOMING = 0,
  PREVIOUS = 1,
}

export const BoardCalendarView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:calendar', {
      '{{ societyName }}': society?.name,
    })
  )
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.UPCOMING)
  const tabs = [
    {
      title: translate('societyCalendarListView.activeTabBar.upcomingTitle'),
    },
    {
      title: translate('societyCalendarListView.activeTabBar.previousTitle'),
    },
  ]

  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const { data: boardRoomEventsData, isLoading: isEvensLoading } = useQuery(
    boardRoomEventQueries.getEventsPerSociety(society?._id)
  )

  const getEvents = useCallback((): {
    [key: string]: IBoardRoomEventModel[]
  } => {
    const now = new Date()
    const _boardRoomEvents = boardRoomEventsData || []
    return _boardRoomEvents.reduce(
      (
        prev: {
          [key: string]: IBoardRoomEventModel[]
        },
        event
      ) => {
        const group = new Date(event.endDate) < now ? 'previous' : 'upcoming'
        prev[group].push(event)
        return prev
      },
      { previous: [], upcoming: [] }
    )
  }, [boardRoomEventsData])

  const segmentedEvents = getEvents()

  const events =
    activeTab === ActiveTab.UPCOMING
      ? segmentedEvents.upcoming.reverse()
      : segmentedEvents.previous

  const loading = isEvensLoading

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('boardCalendarView.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        tabBar={{ tabs, setActiveTab, activeTab }}
        buttons={[
          {
            label: translate('societyCalendarListView.buttons.create'),
            variant: ButtonVariant.PRIMARY,
            icon: IconChoices.PLUS_SIGN,
            onClick: openCreateModal,
          },
        ]}
      >
        <GetToKnow
          className="mb-4"
          title={translate('getToKnowScreen.screens.calendar.title')}
          description={translate(
            'getToKnowScreen.screens.calendar.description',
            { context: i18nSocietyType(society?.societyTypeDisplayName) }
          )}
          getToKnowId="calendar"
        />
        <CalendarList
          events={events}
          userIsAdmin
          boardRoomEvent
          loading={loading}
        />
      </ViewBase>
      {showCreateModal && (
        <CreateUpdateEventModal
          show={showCreateModal}
          close={closeCreateModal}
          boardRoomEvent
        />
      )}
    </ErrorBoundary>
  )
}
